import { useMemo } from 'react';

// Single source of truth for "what is the user analyzing right now?"
// across every analysis page. The header title, the green scope bar,
// and any "facilities selected" caption all read from this so they
// stay in lock-step regardless of which scope source the user used:
//
//   1. activeCustomerId   - in-page Customer dropdown
//   2. activeFilterId     - in-page Saved Filter dropdown
//   3. selectedFacilityIds (with neither of the above) - Custom Filter modal
//   4. none of the above  - "All VitalEngine Facilities"
//
// Pages with URL-driven scope (Customer Data Analysis arrives via
// ?customerId= / ?filterId=) handle that case separately and pass
// the resolved name in via `urlScope`. URL scope wins over in-page
// picks since it's how the user got to the page.
//
// Returns a stable object describing the current scope:
//   { kind, name, viewingLabel, facilityCount, bannerClass,
//     clear, clearLabel, clearTitle }
//
// `clear` is a function the Clear button calls; `kind === 'none'`
// means no scope is active (caller should hide the green Viewing bar
// in that case).
export function useCurrentScope({
  activeCustomerId,
  activeFilterId,
  selectedFacilityIds,
  customers = [],
  savedFilters = [],
  setActiveCustomerId,
  setActiveFilterId,
  setSelectedFacilityIds,
  // Optional: clear the global cross-page scope (the one that drives
  // StickyScopeBar and lets a customer scope follow the user from
  // page to page). If provided, the Clear button on every variant
  // will call it after wiping local state — without this, "Clear" on
  // one page leaves the same scope sticky on the next.
  clearGlobalScope = null,
  // Optional: if the page has its own URL-driven scope context (like
  // CustomerData's groupContext / filterContext), pass it in here and
  // it takes precedence over the in-page picks below.
  urlScope = null,
}) {
  return useMemo(() => {
    if (urlScope) return urlScope;

    const callGlobalClear = () => { if (clearGlobalScope) clearGlobalScope(); };

    if (activeCustomerId) {
      const c = customers.find(x => String(x.id) === String(activeCustomerId));
      if (c) {
        return {
          kind: 'customer',
          name: c.name,
          viewingLabel: 'Viewing:',
          facilityCount: selectedFacilityIds?.size || 0,
          bannerClass: 'scope-bar',
          clear: () => {
            setActiveCustomerId && setActiveCustomerId('');
            setSelectedFacilityIds && setSelectedFacilityIds(new Set());
            callGlobalClear();
          },
          clearLabel: 'Clear customer',
          clearTitle: 'View the facilities in this customer',
        };
      }
    }

    if (activeFilterId) {
      const sf = savedFilters.find(x => String(x.id) === String(activeFilterId));
      if (sf) {
        return {
          kind: 'filter',
          name: sf.name,
          viewingLabel: 'Viewing saved filter:',
          facilityCount: selectedFacilityIds?.size || 0,
          bannerClass: 'scope-bar scope-bar-filter',
          clear: () => {
            setActiveFilterId && setActiveFilterId('');
            setSelectedFacilityIds && setSelectedFacilityIds(new Set());
            callGlobalClear();
          },
          clearLabel: 'Clear filter',
          clearTitle: 'View the facilities in this filter',
        };
      }
    }

    if (selectedFacilityIds && selectedFacilityIds.size > 0) {
      const n = selectedFacilityIds.size;
      return {
        kind: 'custom',
        name: `Custom selection (${n} facilit${n === 1 ? 'y' : 'ies'})`,
        viewingLabel: 'Viewing custom selection:',
        facilityCount: n,
        bannerClass: 'scope-bar scope-bar-filter',
        clear: () => {
          setSelectedFacilityIds && setSelectedFacilityIds(new Set());
          setActiveFilterId && setActiveFilterId('');
          setActiveCustomerId && setActiveCustomerId('');
          callGlobalClear();
        },
        clearLabel: 'Clear selection',
        clearTitle: 'View the selected facilities',
      };
    }

    return {
      kind: 'none',
      name: 'All VitalEngine Facilities',
      viewingLabel: null,
      facilityCount: 0,
      bannerClass: null,
      clear: null,
      clearLabel: null,
      clearTitle: null,
    };
  }, [
    urlScope,
    activeCustomerId,
    activeFilterId,
    selectedFacilityIds,
    customers,
    savedFilters,
    setActiveCustomerId,
    setActiveFilterId,
    setSelectedFacilityIds,
    clearGlobalScope,
  ]);
}

export default useCurrentScope;
